import type { AssistantMessage, MessageAttachment } from './types';

/**
 * How a conversation export was delivered to the user.
 */
export type ConversationExportOutcome = 'copied' | 'downloaded';

const canCopyText = (): boolean =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  window.isSecureContext === true &&
  typeof navigator.clipboard?.writeText === 'function';

const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Release on the next tick so the click has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const formatLink = (link: { label: string; path: string }): string => `${link.label} (${link.path})`;

/**
 * Renders the interactive content attached to a message (breakdown rows,
 * report/navigation links, bulk-edit previews, etc.) as plain text so the
 * clipboard/text transcript doesn't silently drop it -- only `message.text`
 * used to be included, which meant staff copying a conversation to file a
 * ticket lost the actual data (counts, links, proposed changes) the message
 * was about.
 */
const formatAttachment = (attachment: MessageAttachment): string | null => {
  switch (attachment.kind) {
    case 'choices':
      return attachment.options.length
        ? `Options: ${attachment.options.map((option) => option.label).join(', ')}`
        : null;
    case 'locations':
      return attachment.candidates.length
        ? `Matching locations: ${attachment.candidates.map((candidate) => candidate.name).join(', ')}`
        : null;
    case 'draft':
      return `Draft:\n${attachment.text}`;
    case 'confirm':
      return `Confirm: ${attachment.fieldsChanged} field(s) changed`;
    case 'navigate':
      return `Link: ${formatLink({ label: attachment.label, path: attachment.path })}`;
    case 'breakdown': {
      const lines = attachment.rows.map((row) => {
        const linkText = row.link ? ` (${formatLink(row.link)})` : '';
        return `  ${row.label}: ${row.value}${linkText}`;
      });
      if (attachment.link) lines.push(`  ${formatLink(attachment.link)}`);
      return lines.length ? lines.join('\n') : null;
    }
    case 'preview': {
      const { preview } = attachment;
      const lines = [
        `Preview: ${preview.rowsValid}/${preview.rowsProcessed} row(s) valid, ` +
          `${preview.fieldsChanged} field(s) changing across ${preview.locationsWithChanges} location(s)`,
      ];
      preview.rows.forEach((row) => {
        if (row.changes.length === 0) return;
        const label = row.locationName ?? `Row ${row.row}`;
        row.changes.forEach((change) => {
          lines.push(`  ${label} - ${change.field}: ${change.oldValue ?? '(empty)'} -> ${change.newValue ?? '(empty)'}`);
        });
      });
      preview.fileErrors.forEach((issue) => lines.push(`  Error (row ${issue.row}): ${issue.message}`));
      return lines.join('\n');
    }
  }
};

const toTranscript = (messages: AssistantMessage[]): string =>
  messages
    .map((message, index) => {
      const speaker = message.role === 'assistant' ? 'Sentry' : 'You';
      const attachmentText = message.attachment ? formatAttachment(message.attachment) : null;
      const body = attachmentText ? `${message.text}\n${attachmentText}` : message.text;
      return `${index + 1}. ${speaker}\n${body}`;
    })
    .join('\n\n');

/**
 * Exports the conversation history as text for copy/paste, with JSON download
 * fallback where clipboard text APIs are unavailable.
 */
export const captureAssistantPanel = async (
  messages: AssistantMessage[],
): Promise<ConversationExportOutcome> => {
  const transcript = toTranscript(messages);
  const payload = {
    exportedAt: new Date().toISOString(),
    transcript,
    messages: messages.map(({ id, role, text, attachment, resolved }) => ({
      id,
      role,
      text,
      attachment,
      resolved,
    })),
  };

  if (canCopyText()) {
    try {
      await navigator.clipboard.writeText(transcript);
      return 'copied';
    } catch {
      // Permission denied or transient failure — fall through to file download.
    }
  }

  downloadBlob(
    new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
    'sentry-conversation.json',
  );
  return 'downloaded';
};
